import { useAgentChat } from '@/contexts/AgentChatContext';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { Separator } from '@superfunnel/ui-core/v2/Separator';
import { Tabs, TabsList, TabsTrigger } from '@superfunnel/ui-core/v2/Tabs';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@superfunnel/ui-core/v2/ToggleGroup';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { classNames } from '@superfunnel/utils';
import {
  ArrowLeft,
  Computer,
  EditPencil,
  Eye,
  MoreVert,
  Plus,
  Settings,
  SidebarCollapse,
  SmartphoneDevice,
} from 'iconoir-react';
import { BarChart3, Check, GitBranch, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  EditorDevice,
  EditorMode,
  useHtmlStepperEditor,
} from './HtmlStepperEditorContext';
import { PublishPopover } from './PublishPopover';

export const HtmlStepperEditorHeader = () => {
  const { resetAgentSession } = useAgentChat();
  const { state, data, modals, onClose } = useHtmlStepperEditor();
  const {
    selectedFormId,
    setSelectedFormId,
    sidebarCollapsed,
    setSidebarCollapsed,
    device,
    setDevice,
    editorMode,
    setEditorMode,
  } = state;
  const {
    forms,
    hasChanges,
    saving,
    lastSavedAt,
    publishing,
    publish,
    discarding,
    discardChanges,
    canDiscard,
    canRevert,
    canForward,
    revertLatestUpdate,
    forwardRevert,
  } = data;
  const { setPageSettingsModalOpen } = modals;

  // Relative time for last saved
  const [relativeTime, setRelativeTime] = useState<string>('');

  // Update relative time every 30 seconds
  useEffect(() => {
    if (!lastSavedAt) {
      setRelativeTime('');
      return;
    }

    const updateTime = () => {
      const now = new Date();
      const diffMs = now.getTime() - lastSavedAt.getTime();
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHour = Math.floor(diffMin / 60);

      if (diffSec < 60) {
        setRelativeTime('just now');
      } else if (diffMin < 60) {
        setRelativeTime(`${diffMin} min ago`);
      } else if (diffHour < 24) {
        setRelativeTime(`${diffHour} hour${diffHour > 1 ? 's' : ''} ago`);
      } else {
        setRelativeTime(lastSavedAt.toLocaleDateString());
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 30000);
    return () => clearInterval(interval);
  }, [lastSavedAt]);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleOpenQuizSettings = () => {
    setPageSettingsModalOpen(true);
  };

  const handleCloseEditor = () => {
    // TODO: Check for unsaved changes
    onClose();
  };

  const handlePublish = async () => {
    if (!selectedFormId) return;
    await publish(selectedFormId);
  };

  const handleDiscard = async () => {
    if (!selectedFormId) return;
    await discardChanges(selectedFormId);
  };

  return (
    <div className="fixed top-0 right-0 left-0 z-50 flex items-start justify-start">
      {/* Left side - AI Chat header area */}
      <div
        className={classNames(
          'flex items-center justify-between gap-3 bg-[#F7F5FF] py-2 transition-all duration-300 ease-in-out',
          sidebarCollapsed
            ? 'w-0 overflow-hidden px-1 opacity-0'
            : 'min-h-13 w-[384px] shrink-0 px-2',
        )}
      >
        <div className="flex-1 shrink overflow-hidden">
          <Tooltip disableHoverableContent>
            <TooltipTrigger asChild>
              <Button
                variant="default"
                color="white"
                size="icon-lg"
                onClick={handleCloseEditor}
              >
                <ArrowLeft className="size-3.5" strokeWidth={2} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Back</TooltipContent>
          </Tooltip>
        </div>

        <Tooltip disableHoverableContent>
          <TooltipTrigger asChild>
            <Button
              variant="default"
              color="white"
              size="sm"
              onClick={() => {
                resetAgentSession();
              }}
              title="Reset Agent Session"
            >
              <Plus className="size-4.5" />
              <span className="text-xs font-medium">New Chat</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>New Chat</p>
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Main header area */}
      <div className="flex shrink grow items-center justify-between gap-3 overflow-scroll bg-white px-2 py-1">
        <div className={classNames('flex items-center justify-start gap-1')}>
          {sidebarCollapsed && (
            <>
              <Tooltip disableHoverableContent>
                <TooltipTrigger asChild>
                  <Button
                    variant="default"
                    color="white"
                    size="icon-lg"
                    onClick={handleCloseEditor}
                  >
                    <ArrowLeft className="size-4" strokeWidth={2} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Back</TooltipContent>
              </Tooltip>
              <Separator orientation="vertical" className="h-6" />
            </>
          )}

          <Button
            variant="default"
            color="white"
            size="icon-lg"
            onClick={toggleSidebar}
          >
            <SidebarCollapse
              className={`h-5 w-5 transition-transform duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`}
            />
          </Button>

          {/* Settings button */}
          <Button
            variant="default"
            color="white"
            size="icon-lg"
            onClick={handleOpenQuizSettings}
          >
            <Settings className="h-5 w-5" />
          </Button>

          {/* Form selector*/}
          <Separator orientation="vertical" className="me-2 h-6" />
          <Select
            value={selectedFormId ?? ''}
            onValueChange={setSelectedFormId}
          >
            <SelectTrigger className="max-w-45" size="sm">
              <SelectValue placeholder="Select form" />
            </SelectTrigger>
            <SelectContent>
              {forms.map((formDoc) => (
                <SelectItem key={formDoc.id} value={formDoc.id}>
                  {formDoc.data().name || formDoc.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Save Status Indicator */}
          <div
            className={classNames(
              'h-8 w-22 gap-1',
              saving || lastSavedAt
                ? 'flex items-center'
                : 'pointer-events-none invisible',
            )}
          >
            <Separator orientation="vertical" className="ms-2 h-6" />
            <div className="flex items-center gap-1.5 px-2 text-sm text-gray-500">
              {saving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span className="animate-pulse">Saving...</span>
                </>
              ) : (
                <Tooltip disableHoverableContent>
                  <TooltipTrigger asChild>
                    <div className="flex cursor-default items-center gap-1.5 transition-opacity duration-300">
                      <Check className="h-3.5 w-3.5 text-green-600" />
                      <span className="text-green-600">Saved</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Saved {relativeTime}</TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        {/* Center - Mode tabs and device selector */}
        <div className="flex gap-2">
          {/* Device selector - only visible in editor/preview mode */}

          <Tabs
            value={device}
            onValueChange={(t) => setDevice(t as EditorDevice)}
          >
            <TabsList>
              <Tooltip disableHoverableContent>
                <TabsTrigger
                  value="desktop"
                  disabled={editorMode === 'logic'}
                  asChild
                >
                  <TooltipTrigger>
                    <Computer
                      className={classNames(
                        'text-base-500 h-4 w-4',
                        device === 'desktop' && 'text-base-900',
                      )}
                    />
                  </TooltipTrigger>
                </TabsTrigger>
                <TooltipContent>Desktop</TooltipContent>
              </Tooltip>
              <Tooltip disableHoverableContent>
                <TabsTrigger
                  value="tablet"
                  disabled={editorMode === 'logic'}
                  asChild
                >
                  <TooltipTrigger>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 14.6667V12M2 12H14M12.6667 1.33332H3.33333C2.59695 1.33332 2 1.93028 2 2.66666L2 13.3333C2 14.0697 2.59695 14.6667 3.33333 14.6667H12.6667C13.403 14.6667 14 14.0697 14 13.3333V2.66666C14 1.93028 13.403 1.33332 12.6667 1.33332Z"
                        stroke={device === 'tablet' ? '#141C25' : '#637083'}
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </TooltipTrigger>
                </TabsTrigger>
                <TooltipContent>Tablet</TooltipContent>
              </Tooltip>
              <Tooltip disableHoverableContent>
                <TabsTrigger
                  value="mobile"
                  disabled={editorMode === 'logic'}
                  asChild
                >
                  <TooltipTrigger>
                    <SmartphoneDevice
                      className={classNames(
                        'text-base-500 h-4 w-4',
                        device === 'mobile' && 'text-base-900',
                      )}
                    />
                  </TooltipTrigger>
                </TabsTrigger>
                <TooltipContent>Mobile</TooltipContent>
              </Tooltip>
            </TabsList>
          </Tabs>

          {/* Editor mode selector */}
          <Tabs
            value={editorMode}
            onValueChange={(t) => setEditorMode(t as EditorMode)}
          >
            <TabsList>
              <Tooltip disableHoverableContent>
                <TabsTrigger value="editor" asChild>
                  <TooltipTrigger>
                    <EditPencil
                      className={classNames(
                        'size-4',
                        editorMode === 'editor'
                          ? 'text-base-900'
                          : 'text-base-500',
                      )}
                    />
                  </TooltipTrigger>
                </TabsTrigger>
                <TooltipContent>Editor</TooltipContent>
              </Tooltip>
              <Tooltip disableHoverableContent>
                <TabsTrigger value="preview" asChild>
                  <TooltipTrigger>
                    <Eye
                      className={classNames(
                        'h-4 w-4',
                        editorMode === 'preview'
                          ? 'text-base-900'
                          : 'text-base-500',
                      )}
                    />
                  </TooltipTrigger>
                </TabsTrigger>
                <TooltipContent>Preview</TooltipContent>
              </Tooltip>
              <Tooltip disableHoverableContent>
                <TabsTrigger value="logic" asChild>
                  <TooltipTrigger>
                    <GitBranch
                      className={classNames(
                        'h-4 w-4',
                        editorMode === 'logic'
                          ? 'text-base-900'
                          : 'text-base-500',
                      )}
                    />
                  </TooltipTrigger>
                </TabsTrigger>
                <TooltipContent>Logic</TooltipContent>
              </Tooltip>
              <Tooltip disableHoverableContent>
                <TabsTrigger value="results" asChild>
                  <TooltipTrigger>
                    <BarChart3
                      className={classNames(
                        'h-4 w-4',
                        editorMode === 'results'
                          ? 'text-base-900'
                          : 'text-base-500',
                      )}
                    />
                  </TooltipTrigger>
                </TabsTrigger>
                <TooltipContent>Results</TooltipContent>
              </Tooltip>
            </TabsList>
          </Tabs>
        </div>

        {/* Right side - Undo/Redo and Save/Discard */}
        <div className="flex items-center gap-2">
          <ToggleGroup variant="outline" size={'sm'} type="multiple">
            <ToggleGroupItem
              value="undo"
              disabled={!canRevert}
              aria-label="Undo"
              onClick={() => {
                revertLatestUpdate();
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.0013 5.33333H10.0013C10.0013 5.33333 13.3346 5.33333 13.3346 8.47059C13.3346 12 10.0013 12 10.0013 12H4.19178M5.0013 7.66667L2.66797 5.33333L5.0013 3"
                  stroke="#637083"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ToggleGroupItem>
            <ToggleGroupItem
              value="redo"
              disabled={!canForward}
              aria-label="Redo"
              onClick={() => {
                forwardRevert();
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.9987 5.33333H5.9987C5.9987 5.33333 2.66537 5.33333 2.66537 8.47059C2.66537 12 5.9987 12 5.9987 12H11.8082M10.9987 7.66667L13.332 5.33333L10.9987 3"
                  stroke="#637083"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ToggleGroupItem>
          </ToggleGroup>

          <Button
            variant="outline"
            size={'sm'}
            onClick={handleDiscard}
            loading={discarding}
            disabled={!canDiscard || saving || publishing || discarding}
          >
            Discard
          </Button>
          <PublishPopover
            hasChanges={hasChanges}
            saving={saving}
            publishing={publishing}
            discarding={discarding}
            onPublish={handlePublish}
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon-lg">
                <MoreVert className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* <DropdownMenuItem>Duplicate</DropdownMenuItem> */}
              {/* <DropdownMenuItem>Export</DropdownMenuItem> */}
              <DropdownMenuItem>View History (coming soon)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};
